export interface ApiErrorDetail {
  code?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail: ApiErrorDetail = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  get code(): string | undefined {
    return this.detail.code;
  }
}

const CSRF_COOKIE = "tv_csrf";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function csrfToken(): string {
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : "";
}

/** JSON request against the backend under /api. Session cookies are sent; unsafe methods carry the CSRF token. */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  if (UNSAFE.has(method)) headers.set("X-CSRF-Token", csrfToken());
  const response = await fetch(`/api${path}`, { credentials: "same-origin", ...init, method, headers });
  if (!response.ok) {
    let detail: ApiErrorDetail = {};
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
        detail = body.detail as ApiErrorDetail;
      } else if (Array.isArray(body.detail)) {
        detail = { code: "validation_error", errors: body.detail };
      }
    } catch {
      // not JSON
    }
    throw new ApiError(response.status, `${response.status} ${response.statusText}`, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  apiFetch<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
export const apiPut = <T>(path: string, body: unknown): Promise<T> =>
  apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) });
export const apiPatch = <T>(path: string, body: unknown): Promise<T> =>
  apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) });
export const apiDelete = (path: string): Promise<void> => apiFetch<void>(path, { method: "DELETE" });

export interface Health {
  status: "ok" | "degraded";
  version: string;
  database: { ok: boolean; version: string | null; problem?: string | null };
}

export const getHealth = (): Promise<Health> => apiFetch<Health>("/health");

export interface LastImport {
  id: number;
  status: "succeeded" | "failed" | "running" | "rolled_back";
  finished_at: string | null;
  tickets_new: number;
  tickets_updated: number;
}

export interface ArchiveSummary {
  tickets: number;
  articles: number;
  queues: number;
  first_ticket_at: string | null;
  last_ticket_at: string | null;
  last_import: LastImport | null;
}

export const getArchiveSummary = (): Promise<ArchiveSummary> => apiFetch<ArchiveSummary>("/archive/summary");

export const archiveSummaryQuery = { queryKey: ["archive", "summary"], queryFn: getArchiveSummary } as const;
